import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IndustrySwitcher({ user, onIndustryChange, compact = false }) {
  const [industries, setIndustries] = useState([]);
  const [currentIndustry, setCurrentIndustry] = useState(user?.current_industry || user?.industry);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchIndustries();
  }, []);

  useEffect(() => {
    setCurrentIndustry(user?.current_industry || user?.industry);
  }, [user]);

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${API}/industries`);
      setIndustries(response.data.industries);
    } catch (err) {
      console.error('Failed to fetch industries:', err);
    }
  };

  const handleIndustryChange = async (industry) => {
    try {
      await axios.put(`${API}/users/${user.id}/current-industry`, {
        industry: industry
      });
      
      setCurrentIndustry(industry);
      setIsOpen(false);
      
      // Notify parent component
      if (onIndustryChange) {
        onIndustryChange(industry);
      }
    } catch (err) {
      console.error('Failed to update industry:', err);
      alert('Failed to switch industry');
    }
  };

  // Only show to admin users
  if (!user || user.role !== 'admin') {
    return null;
  }

  if (compact) {
    // Compact version for header
    return (
      <div className="relative z-[100]">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-4 py-2 accent-gradient text-white rounded-lg transition"
        >
          <span className="text-sm font-medium">🏭 {currentIndustry || 'Select Industry'}</span>
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-[99]" 
              onClick={() => setIsOpen(false)}
            />
            <div 
              className="absolute right-0 mt-2 w-72 border-2 rounded-lg shadow-2xl z-[101] max-h-[450px] overflow-y-auto custom-scrollbar"
              style={{
                backgroundColor: 'var(--secondary-bg)',
                borderColor: 'var(--accent-from)',
                scrollbarWidth: 'thin'
              }}
            >
              <div className="sticky top-0 border-b px-3 py-3 z-10" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                <div className="text-xs font-semibold accent-text uppercase">
                  👑 Admin: Switch Industry
                </div>
              </div>
              <div className="p-2">
                {industries.map((industry) => (
                  <button
                    key={industry}
                    onClick={() => handleIndustryChange(industry)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition mb-1 ${
                      currentIndustry === industry
                        ? 'accent-gradient text-white shadow-lg'
                        : ''
                    }`}
                    style={currentIndustry !== industry ? {
                      color: 'var(--text-secondary)',
                      backgroundColor: 'transparent'
                    } : {}}
                    onMouseEnter={(e) => {
                      if (currentIndustry !== industry) {
                        e.target.style.backgroundColor = 'var(--hover-bg)';
                        e.target.style.color = 'var(--text-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentIndustry !== industry) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--text-secondary)';
                      }
                    }}
                  >
                    {industry}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Full version for settings
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-orange-500/20 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
          👑 Admin Industry Switcher
        </h3>
        <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full">
          ADMIN ONLY
        </span>
      </div>
      <p className="text-gray-400 text-sm mb-4">
        As an admin, you can switch between any industry to test content generation.
      </p>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Current Industry: <span className="text-orange-400 font-bold">{currentIndustry || user?.industry}</span>
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {industries.map((industry) => (
          <button
            key={industry}
            onClick={() => handleIndustryChange(industry)}
            className={`p-3 rounded-lg border-2 text-sm font-medium transition ${
              currentIndustry === industry
                ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-orange-500/50'
            }`}
          >
            {industry}
          </button>
        ))}
      </div>
    </div>
  );
}

export default IndustrySwitcher;
